import {SagaIterator} from 'redux-saga';
import isEmail from 'validator/lib/isEmail';
import md5 from 'blueimp-md5';
import {reduce, prop} from 'ramda';
import {pushDataLayer} from '@optimaxdev/analytics/mobile';

import type {MapType} from 'constants/flow/flowTypes';
import {DATALAYER_EMAIL} from 'constants/analytics/dataLayer';

import type {AnalyticsType, GaEventType} from 'reducers/analytics/analytics/analyticsType';
import {sendBrontoParams} from 'libs/bronto/bronto';
import {call} from 'redux-saga/effects';
import {sessionStore} from 'libs/storage/storage';
import type {ActiveFilterType, FilterItemsType} from 'reducers/category/filters/filtersType';
import {OAuthType} from 'reducers/oAuth/oAuthType';
import {doTransform} from '../transformers';

/**
 * Check enabled analytics and send data for it
 * @param {object} analyticsSettings - settings and data for all enabled analytics
 */
export function sendAnalytics(analyticsSettings: AnalyticsType) {
    if (typeof analyticsSettings?.bronto !== 'undefined') {
        sendBrontoParams(analyticsSettings.bronto.params, analyticsSettings.bronto.jsId);
    }
}

/**
 * Push data info GTM
 * @param {any} event Current event name
 * @param {boolean} isRaw enables sending event as object
 * @deprecated
 */
export function analytics(event: any, isRaw?: boolean = false) {
    window.dataLayer = window.dataLayer || [];
    const data = isRaw ? event : {event};
    window.dataLayer.push(data);
}

/**
 * Generate event for GA
 * @param {string|function(MapType<string, string>): string} label - Label string or format function
 * @param {string|MapType<string, string>} value - Value string or object which will be passed to format function.
 * @returns {string} - GA label.
 * @deprecated
 */
export const createGALabel = (
    label: ((MapType<string>) => string) | string,
    value?: MapType<string> | string,
): string => {
    if (typeof label === 'function') return label(value);

    return `${label}${value || ''}`;
};

/**
 * Generate event for GA
 * @param {{action: string, label: string, category: string}} eventDetails - event details
 * @param {string} value - additional
 * @param {boolean} isInteraction - event user interaction
 * @returns {GaEventType} GTM event
 * @deprecated
 */
export const createGAEvent = (
    eventDetails: {
        action: string,
        label: string,
        category: string,
    },
    value?: string,
    isInteraction: boolean = true,
): GaEventType => ({
    event: `GAEventGenericTrigger${isInteraction ? '' : 'Non'}Interaction`,
    eventCategory: eventDetails.category,
    eventAction: eventDetails.action,
    eventLabel: createGALabel(eventDetails.label, value),
});

/**
 * Create event for product page (readers frame)
 * @param {string} action - readers action
 * @return {GaEventType} GTM event
 */
export const createEventForPDPReaders = (action: string): GaEventType => ({
    event: 'GAEventGenericTriggerNonInteraction',
    eventCategory: 'Readers-PDP',
    eventAction: action,
    eventLabel: 'Power',
});

/**
 * Return filter list for DataLayer.
 * @param {Array<FilterItemsType>} availableFilters - Available filters.
 * @param {ActiveFilterType} activeFilters - Active filters.
 * @returns {Object<string, Array<?string>>} Filter list.
 */
export const prepareFilterList = (
    availableFilters: Array<FilterItemsType>,
    activeFilters: ActiveFilterType,
): {
    [string]: Array<?string>,
} =>
    reduce(
        (accumulator, filters) => {
            const {code} = filters;
            const activeFilter = prop(code, activeFilters);
            const preparedFilter = activeFilter ? activeFilter.map(item => item.value || item) : [];
            return {...accumulator, [code]: preparedFilter};
        },
        {},
        availableFilters,
    );

/**
 * Send correct email to dataLayer.
 * @param {string} email - Email.
 */
export function* sendEmailToDataLayer(email: string): SagaIterator {
    const isValidEmail = yield call(isEmail, email);
    if (!isValidEmail) return;
    const emailList = yield call([sessionStore, 'get'], DATALAYER_EMAIL, []);
    const isNewEmail = !emailList.find(emailItem => emailItem === email);
    if (!isNewEmail) return;
    yield call([sessionStore, 'set'], DATALAYER_EMAIL, [...emailList, email]);
    const hashedEmail = yield call(md5, email);
    yield call(
        analytics,
        {
            mktg_email: email,
            mktg_hashed_email: hashedEmail,
        },
        true,
    );
}

/**
 * Send event with customer data
 * @param {string | OAuth2Type} customerData - customer data or just email
 */
export const sendEventToDataLayerForCustomerData = (customerData: string | OAuthType) => {
    if (!customerData) {
        pushDataLayer({
            first_name: '',
            last_name: '',
            email: '',
            MD5_email: '',
            SHA256_email: '',
        });
        return;
    }
    const isOnlyEmail = typeof customerData === 'string';
    pushDataLayer({
        first_name: isOnlyEmail ? '' : customerData.firstname,
        last_name: isOnlyEmail ? '' : customerData.lastname,
        email: isOnlyEmail ? customerData : customerData.email,
        MD5_email: isOnlyEmail ? doTransform(customerData, 'md5') : customerData.md5,
        SHA256_email: isOnlyEmail ? doTransform(customerData, 'sha256') : customerData.sha256,
    });
};
